import React, { ChangeEvent } from 'react';
import MediaQuery from 'react-responsive';

import { ArticleFromDom } from '../../../../Integrations/ProductActionsIntegrations/Types';
import { AccountContext } from '../../../Context/AccountContext';
import { LocalCartContext } from '../../../Context/LocalCartContext';
import { ShopwareCartContext } from '../../../Context/ShopwareCartContext';
import { isVoucher } from '../../../Utils';
import { ActionType, CartItemType, LocalCart, ShopwareCart } from '../../Types';
import { VoucherFromDom } from '../Voucher/VoucherActions';

import './AddToCart.scss';

interface Props {
    article: ArticleFromDom | VoucherFromDom;
    blocked?: boolean;
    hideQuantity?: boolean;
}

enum Status {
    NOT_ADDED,
    ADDED,
    BLOCKED,
    BLOCKED_BY_VOUCHER,
}

const notInCartLabel = 'in den Warenkorb';
const inCartLabel = 'im Warenkorb';
const cartShortLabel = 'Warenkorb';
const blockedByVoucherHelp = 'Leider ist es nicht möglich Gutscheine und Artikel gleichzeitig zu kaufen';
const blockedHelp = 'Dieses Produkt kann um diesen Preis nicht bestellt werden';
const inCartHelp = 'Dieses Produkt befindet sich bereits im Warenkorb';
const notInCartHelp = 'Dieses Produkt in den Warenkorb legen';

export default function AddToCart(props: Props) {
    const initialCartItem: CartItemType = {
        articleNumber: props.article.articleNumber,
        link: props.article.link,
        price: props.article.price,
        specialPrice: props.article.specialPrice,
        priceInCents: props.article.priceInCents,
        countryCode: props.article.countryCode,
        quantity: 1,
        title: props.article.title,
        imageUri: props.article.imageUri,
        volume: props.article.volume,
        type: props.article.type,
        maxBuyableAmount: props.article.maxBuyableAmount,
        // TODO: outsource this to separate type? Might require a lot of refactoring
        voucher_design_name: isVoucher(props.article) ? props.article.designName : '',
    };

    const { isLoggedIn } = React.useContext(AccountContext);
    let cart: ShopwareCart | LocalCart;
    let activeItems: CartItemType[];
    let updateCartItems: (updateItem: CartItemType, action: ActionType, customerId?: string) => void;

    const context = isLoggedIn ? ShopwareCartContext : LocalCartContext;
    ({ cart, updateCartItems, activeItems } = React.useContext(context));
    const [status, setStatus] = React.useState<Status>(Status.NOT_ADDED);
    const [cartItem, updateCartItem] = React.useState<CartItemType>(initialCartItem);

    React.useEffect(() => {
        const activeCartItem = activeItems.find((item) => item.articleNumber === props.article.articleNumber);
        if (activeCartItem) {
            setStatus(Status.ADDED);
            updateCartItem(activeCartItem);
        } else {
            setStatus(Status.NOT_ADDED);
            updateCartItem(initialCartItem);
        }

        // vouchers and products must never be placed in the cart at the same time
        if (
            (isVoucher(props.article) === false && activeItems.some((item) => isVoucher(item))) ||
            (isVoucher(props.article) === true && activeItems.some((item) => item.type === 'product'))
        ) {
            setStatus(Status.BLOCKED_BY_VOUCHER);
        }

        if (props.article.priceInCents === 0 || Number.isNaN(props.article.priceInCents) || props.blocked === true) {
            setStatus(Status.BLOCKED);
        }
    }, [cart, props.article, props.blocked]);

    const onButtonClick = () => {
        if (status === Status.NOT_ADDED) {
            updateCartItems(cartItem, ActionType.Add);
        }
    };

    const onQuantityChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const newCartItem = { ...cartItem };
        if (e.target) {
            newCartItem.quantity = parseInt(e.target.value);

            if (activeItems.some((item) => item.articleNumber === cartItem.articleNumber)) {
                // update item within cart, since it has already been added
                updateCartItems(newCartItem, ActionType.Update);
            } else {
                // only update item in local state, since item is not in cart yet
                updateCartItem(newCartItem);
            }
        }
    };

    const inCartClass = () => {
        if (activeItems.some((item) => item.articleNumber === cartItem.articleNumber)) {
            return 'add-to-cart__button--in-cart';
        }

        return '';
    };

    const ariaLabel = (): string => {
        switch (status) {
            case Status.ADDED:
                return inCartHelp;
            case Status.NOT_ADDED:
                return notInCartHelp;
            case Status.BLOCKED_BY_VOUCHER:
                return blockedByVoucherHelp;
            case Status.BLOCKED:
                return blockedHelp;
            default:
                return assertNever(status);
        }
    };

    return (
        <div className="add-to-cart">
            {props.hideQuantity === true ? null : (
                <select
                    className="add-to-cart__quantity"
                    onChange={onQuantityChange}
                    value={cartItem.quantity.toString()}
                >
                    {[...Array(props.article.maxBuyableAmount + 1).keys()].map((key: number) => (
                        <option key={key} value={key}>
                            {key}
                        </option>
                    ))}
                </select>
            )}
            <button
                disabled={status === Status.BLOCKED_BY_VOUCHER || status === Status.BLOCKED}
                type="button"
                className={'add-to-cart__button ' + inCartClass()}
                onClick={onButtonClick}
                aria-label={ariaLabel()}
                data-balloon-pos="down"
            >
                <svg className="add-to-action__icon add-to-action__icon--normal icon">
                    <use xlinkHref={'#icon-shopping-cart' + (status === Status.ADDED ? '-full' : '-empty')} />
                </svg>
                <MediaQuery minWidth={365}>
                    {(matches: boolean) =>
                        matches
                            ? status !== Status.ADDED
                                ? notInCartLabel
                                : inCartLabel
                            : status !== Status.ADDED
                              ? cartShortLabel
                              : inCartLabel
                    }
                </MediaQuery>
            </button>
        </div>
    );
}

function assertNever(x: never): never {
    throw new Error('Unexpected object: ' + x);
}
